import { useMemo } from 'react';
import { format } from 'date-fns';
import { formatCalendarDate } from '../utils/date-time';
import { useBookingStore } from '../store/useBookingStore';
import { useServiceQuery } from '../queries';

/**
 * Hook to get current service from store + query
 */
export function useService() {
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	return useServiceQuery(
		{ id: currentServiceId },
		{ enabled: currentServiceId > 0 }
	);
}

/**
 * Hook to compute if the service is a date range booking
 */
export function useIsDateRange(): boolean {
	const { data: service } = useService();
	return useMemo(() => {
		return Boolean(service?.blockSize && service.blockSize >= 1440);
	}, [service?.blockSize]);
}

/**
 * Hook to compute the booking time value string for the hidden input.
 * Always outputs range format: "start|end"
 */
export function useBookingTimeValue(): string {
	const { data: service } = useService();
	const selectedRange = useBookingStore((state) => state.selectedRange);

	return useMemo(() => {
		if (!service || !selectedRange?.from || !selectedRange?.to) {
			return '';
		}

		// Format dates in YYYY-MM-DD format
		const startDate = formatCalendarDate(selectedRange.from);
		const endDate = formatCalendarDate(selectedRange.to);

		// Format times in HH:MM:SS format using date-fns
		const startTime = format(selectedRange.from, 'HH:mm:ss');
		const endTime = format(selectedRange.to, 'HH:mm:ss');

		// Always return range format - backend will normalize
		return `${startDate} ${startTime}|${endDate} ${endTime}`;
	}, [service, selectedRange]);
}

/**
 * Hook to get the selected date (computed from range)
 */
export function useSelectedDate(): Date | undefined {
	const selectedRange = useBookingStore((state) => state.selectedRange);
	return useMemo(() => selectedRange?.from, [selectedRange]);
}

/**
 * Hook to get the selected time (computed from range)
 */
export function useSelectedTime(): string | undefined {
	const selectedRange = useBookingStore((state) => state.selectedRange);
	return useMemo(() => {
		const from = selectedRange?.from;
		if (!from) {
			return undefined;
		}
		// Return HH:MM format using date-fns
		return format(from, 'HH:mm');
	}, [selectedRange]);
}

/**
 * Hook to get the selected duration (computed from range)
 */
export function useSelectedDuration(): number {
	const selectedRange = useBookingStore((state) => state.selectedRange);
	return useMemo(() => {
		if (!selectedRange?.from || !selectedRange?.to) {
			return 0;
		}
		// Calculate duration in minutes
		return Math.round(
			(selectedRange.to.getTime() - selectedRange.from.getTime()) /
				(1000 * 60)
		);
	}, [selectedRange]);
}

/**
 * Hook to compute the effective duration
 */
export function useEffectiveDuration(): number {
	const { data: service } = useService();
	const selectedDuration = useSelectedDuration();

	return useMemo(() => {
		if (selectedDuration > 0) {
			return selectedDuration;
		}
		return service?.minDuration ?? service?.blockSize ?? 0;
	}, [selectedDuration, service?.minDuration, service?.blockSize]);
}
